import classNames from 'classnames'
import {
  Check,
  ChevronDown,
  Plus,
  SquareDashed,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import Avatar from '@/elements/Avatar'
import Label from '@/elements/Label'
import Text from '@/elements/Text'
import FormInput from '@/elements/FormInput'
import Flex from '@/elements/Flex'
import DropdownMenu from '@/components/DropdownMenu'
import { useDropdown } from '@/hooks/useDropdown'

import './FormSelect.scss'

interface FormSelectProps<T> {
  name: string
  values: T[]
  options: T[]
  onChange: (values: T[]) => void
  optionLabel: (item: T, selected: boolean) => string | React.ReactNode
  optionValue: (item: T) => string
  optionIcon?: (item: T) => LucideIcon | React.ComponentType
  optionAvatar?: (item: T) => string
  placeholder?: string
  label?: string
  Icon?: LucideIcon | React.ComponentType
  isMulti?: boolean
  filterBy?: (item: T, search: string) => boolean
  className?: string
  style?: React.CSSProperties
  dirty?: boolean
  error?: string
  disabled?: boolean
  instructions?: string
  allowUnselect?: boolean
  noVerticalScroll?: boolean
  optionsAutoWidth?: boolean
  createCustomValue?: ((search: string) => T) | ((search: string) => string)
}

const FormSelect = <T,>({
  name,
  values,
  options,
  onChange,
  optionLabel,
  optionValue,
  optionIcon,
  optionAvatar,
  placeholder = 'Select an option',
  label,
  Icon,
  isMulti = false,
  filterBy,
  className,
  style,
  dirty = false,
  error,
  disabled,
  instructions,
  allowUnselect = false,
  noVerticalScroll = false,
  optionsAutoWidth = false,
  createCustomValue,
}: FormSelectProps<T>) => {
  const [open, setOpen] = useState(false)
  const [draftValues, setDraftValues] = useState<T[]>(values)
  const [search, setSearch] = useState('')

  useEffect(() => {
    setDraftValues(values)
  }, [values])

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const normalizedSearch = useMemo(
    () => search.trim().toLowerCase(),
    [search]
  )

  const isSelected = useCallback(
    (option: T) =>
      draftValues.some(v => optionValue(v) === optionValue(option)),
    [draftValues, optionValue]
  )

  const saveAndClose = useCallback(() => {
    onChange(draftValues)
    setOpen(false)
  }, [draftValues, onChange])

  const closeWithoutSaving = useCallback(() => {
    setDraftValues(values)
    setOpen(false)
  }, [values])

  const toggleOption = useCallback(
    (option: T) => {
      const selected = isSelected(option)

      if (isMulti) {
        setDraftValues(prev =>
        (selected
          ? prev.filter(v => optionValue(v) !== optionValue(option))
          : [...prev, option])
        )
        return
      }

      if (allowUnselect && selected) {
        onChange([])
      } else {
        onChange([option])
      }

      setSearch('')
      setOpen(false)
    },
    [allowUnselect, isMulti, isSelected, onChange, optionValue]
  )

  const filteredOptions = useMemo(() => {
    if (!filterBy || !normalizedSearch) return options
    return options.filter(option => filterBy(option, search))
  }, [filterBy, search, normalizedSearch, options])

  const hasExactMatch = useMemo(() => {
    if (!normalizedSearch) return false

    return options.some(option => {
      const label = optionLabel(option, false)
      if (typeof label !== 'string') return false

      return label.trim().toLowerCase() === normalizedSearch
    })
  }, [options, optionLabel, normalizedSearch])

  const canCreateCustom = useMemo(() => {
    if (isMulti) return false
    if (!createCustomValue) return false
    if (!normalizedSearch) return false

    return !hasExactMatch
  }, [isMulti, createCustomValue, normalizedSearch, hasExactMatch])

  const selectedIndex = useMemo(() => {
    const index = filteredOptions.findIndex(option =>
      draftValues.some(v => optionValue(v) === optionValue(option))
    )
    if (index === -1) return -1
    return filterBy ? index + 1 : index
  }, [filterBy, filteredOptions, draftValues, optionValue])

  const createIndex = useMemo(() => {
    if (!canCreateCustom) return -1
    return filterBy ? 1 : 0
  }, [canCreateCustom, filterBy])


  const {
    position,
    anchorRef,
    menuRef,
    optionRefs,
    onOptionKeyDown,
  } = useDropdown<HTMLDivElement>({
    open,
    onClose: saveAndClose,
    optionsLength:
      filteredOptions.length +
      (filterBy ? 1 : 0) +
      (canCreateCustom ? 1 : 0),
    onSelect: index => {
      // CREATE OPTION
      if (canCreateCustom && index === createIndex) {
        const custom = createCustomValue!(search)
        onChange([custom as T])
        setSearch('')
        setOpen(false)
        return
      }
      // NORMAL OPTIONS
      const offset =
        (filterBy ? 1 : 0) +
        (canCreateCustom ? 1 : 0)

      const optionIndex = index - offset
      const option = filteredOptions[optionIndex]

      if (option) {
        toggleOption(option)
      }
    },
    initialFocusedIndex: selectedIndex,
    disableInitialFocus: !!filterBy,
  })

  const onTriggerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (filterBy && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      setOpen(true)
      return
    }

    if (!open && (e.key === 'ArrowDown')) {
      e.preventDefault()
      setOpen(true)
      return
    }

    if (e.key === 'Tab' && !open) {
      e.preventDefault()
      setOpen(true)
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setOpen(true)
      return
    }
    onOptionKeyDown(e, -1)
  }, [filterBy, open, onOptionKeyDown])

  const displayValue = useMemo(() => {
    if (!draftValues.length) return placeholder
    if (!isMulti) return optionLabel(draftValues[0], true) || placeholder
    return draftValues.map(v => optionLabel(v, true)).join(', ')
  }, [draftValues, isMulti, optionLabel, placeholder])

  const hasValue = displayValue !== placeholder

  return (
    <>
      <div
        className={classNames('form-field select', className, { dirty })}
        style={style}
      >
        <Label htmlFor={name} hidden={!label}>
          {label}
        </Label>

        <div className='field-inner' ref={anchorRef}>
          {Icon && (
            <Icon size={24} className='field-icon' />
          )}
          <button
            id={name}
            type='button'
            disabled={disabled}
            className={classNames('button dropdown-toggle', {
              'has-value': hasValue,
            })}
            onClick={() => setOpen(o => !o)}
            onKeyDown={onTriggerKeyDown}
          >
            <Flex inline align='center' gap={2}>
              {optionAvatar && !isMulti && draftValues[0] && (
                <Avatar
                  name={optionLabel(draftValues[0], true) as string}
                  src={optionAvatar(draftValues[0])}
                  size='extraSmall'
                />
              )}
              <span>{displayValue}</span>
            </Flex>
            <ChevronDown style={{ width: 18 }} />
          </button>
        </div>

        {instructions && (
          <span className='field-instructions'>{instructions}</span>
        )}
        {error && (
          <span className='field-error'>
            <TriangleAlert size={12} />
            {error}
          </span>
        )}
      </div>

      {open && (
        <DropdownMenu
          open={open}
          position={position}
          title={placeholder}
          menuRef={menuRef as React.RefObject<HTMLDivElement>}
          onClose={saveAndClose}
          closeWithoutSaving={closeWithoutSaving}
          optionsAutoWidth={optionsAutoWidth}
          noVerticalScroll={noVerticalScroll}
          isMulti={isMulti}
          isSearchable={!!filterBy}
        >
          {filterBy && (
            <Flex wfull p={2}>
              <FormInput
                inputRef={el => {
                  optionRefs.current[0] = el
                }}
                name='select-search'
                placeholder='Search...'
                value={search}
                onChangeText={setSearch}
                onKeyDown={e => onOptionKeyDown(e, 0)}
                onClick={e => e.stopPropagation()}
                autoFocus
                style={{ width: '100%' }}
              />
            </Flex>
          )}
          <Flex component='ul' scroll='vertical'>
            {canCreateCustom && (
              <li
                ref={el => {
                  optionRefs.current[filterBy ? 1 : 0] = el
                }}
                tabIndex={-1}
                className='dropdown-item'
                onClick={e => {
                  e.stopPropagation()
                  const custom = createCustomValue!(search)
                  onChange([custom as T])
                  setSearch('')
                  setOpen(false)
                }}
                onKeyDown={e =>
                  onOptionKeyDown(e, filterBy ? 1 : 0)
                }
              >
                <Flex inline align='center' gap={2}>
                  <Plus size={18} />
                  <Text>Create "{search.trim()}"</Text>
                </Flex>
              </li>
            )}

            {filteredOptions.length === 0 && !canCreateCustom ? (
              <Flex p={3}>
                <Text color='black-light-60'>No results found.</Text>
              </Flex>
            ) : (
              filteredOptions.map((option, index) => {
                const selected = isSelected(option)
                const offset =
                  (filterBy ? 1 : 0) + (canCreateCustom ? 1 : 0)

                return (
                  <li
                    key={optionValue(option)}
                    ref={el => {
                      optionRefs.current[index + offset] = el
                    }}
                    tabIndex={-1}
                    className={classNames('dropdown-item', {
                      active: selected,
                    })}
                    onClick={e => {
                      e.stopPropagation()
                      toggleOption(option)
                    }}
                    onKeyDown={e =>
                      onOptionKeyDown(e, index + offset)
                    }
                  >
                    <Flex inline align='center' gap={2}>
                      {optionIcon && (() => {
                        const IconComp =
                          optionIcon(option) || SquareDashed
                        return <IconComp size={18} />
                      })()}
                      {optionAvatar && (
                        <Avatar
                          name={optionLabel(option, false) as string}
                          src={optionAvatar(option)}
                          size='extraSmall'
                        />
                      )}
                      {optionLabel(option, false)}
                    </Flex>
                    {selected && <Check size={18} />}
                  </li>
                )
              })
            )}
          </Flex>
        </DropdownMenu>
      )}
    </>
  )
}

export default FormSelect
